import { Files } from "lucide-react"
import type { GitSummaryResponse } from "../../nestAPIs/git"

function lineTotals(gitStatus?: GitSummaryResponse | null): { insertions: number; deletions: number } {
    return {
        insertions: gitStatus?.total.insertions ?? 0,
        deletions: gitStatus?.total.deletions ?? 0,
    }
}

function workingTreeLabels(gitStatus?: GitSummaryResponse | null): string[] {
    if (!gitStatus?.hasChanges) return []

    const { insertions, deletions } = lineTotals(gitStatus)
    const { filesChanged, unknownFiles = 0 } = gitStatus.total
    const labels = filesChanged > 0 ? [`${filesChanged} changed ${filesChanged === 1 ? "file" : "files"}`] : []
    if (insertions > 0 || deletions > 0) {
        const incomplete = unknownFiles > 0 ? `; line totals unavailable for ${unknownFiles} ${unknownFiles === 1 ? "file" : "files"}` : ""
        labels.push(`${insertions} lines added and ${deletions} lines removed${incomplete}`)
    } else {
        labels.push("line totals unavailable")
    }
    return labels
}

export function gitLogButtonLabel(gitStatus?: GitSummaryResponse | null, taskCommitCount?: number | null): string {
    const details: string[] = []
    if (taskCommitCount !== null && taskCommitCount !== undefined) {
        details.push(`${taskCommitCount} ${taskCommitCount === 1 ? "commit" : "commits"} in task worktree beyond default branch`)
    }
    details.push(...workingTreeLabels(gitStatus))
    return details.length > 0 ? `Open Git Log, ${details.join(", ")}` : "Open Git Log"
}

export function GitChangeIndicator({ gitStatus }: { gitStatus?: GitSummaryResponse | null }) {
    const { insertions, deletions } = lineTotals(gitStatus)
    const hasUnknownLines = (gitStatus?.total.unknownFiles ?? 0) > 0
    if (insertions === 0 && deletions === 0) {
        return gitStatus?.hasChanges ? <span className="h-1.5 w-1.5 shrink-0 bg-primary" aria-hidden="true" data-change-indicator="non-line" /> : null
    }

    return (
        <span className="flex min-w-0 shrink-0 items-center gap-1 font-mono text-[10px] leading-none" aria-hidden="true">
            <span className="text-success">+{insertions}</span>
            <span className="text-error">−{deletions}</span>
            {hasUnknownLines && <span className="text-muted">+?</span>}
        </span>
    )
}

export function GitLogSummary({ gitStatus, taskCommitCount }: { gitStatus?: GitSummaryResponse | null; taskCommitCount?: number | null }) {
    const hasCommitCount = taskCommitCount !== null && taskCommitCount !== undefined
    const filesChanged = gitStatus?.total.filesChanged ?? 0
    if (!hasCommitCount && !gitStatus?.hasChanges) return null

    return (
        <span className="flex min-w-0 shrink-0 items-center gap-1.5 text-[10px] leading-none" aria-hidden="true">
            {hasCommitCount && (
                <span className="shrink-0 font-mono text-muted" data-git-stat="commits">
                    {taskCommitCount}
                </span>
            )}
            {filesChanged > 0 && (
                <span className="flex shrink-0 items-center gap-0.5 text-muted" data-git-stat="files">
                    <Files size={10} />
                    <span className="font-mono">{filesChanged}</span>
                </span>
            )}
            <GitChangeIndicator gitStatus={gitStatus} />
        </span>
    )
}
